import { Component, OnDestroy, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { lastValueFrom, Subscription } from 'rxjs';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar'
import { MatDialog } from '@angular/material/dialog';
import { chatMessageModel } from '../../models/chat-message.model';
import { ChatService } from '../../services/chat.service';
import { TokenStorageService } from '../../services/token-storage.service';
import { MediaService } from '../../services/media.service';
import { AppVisibilityService } from '../../services/app-visibility.service';
import { PushNotificationService } from '../../services/push-notification';
import { ConversationMessage, MediaModel } from '../../models/conversation-history.model';
import { advisorProfileModel } from '../../models/advisor-profile.model';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { ErrorComponent } from '../error/error.component';
import { ErrorViewModel } from '../../models/error-view.model';
import { PushSubscriptionModalComponent } from '../push-subscription-modal/push-subscription-modal.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { AttachmentDialogComponent } from '../attachment-dialog/attachment-dialog.component';
import { MediaType } from '../../models/MediaAttachment';
import { ImageViewerDialogComponent } from '../media-viewer-dialog/image-viewer-dialog.component';
import { senderType } from '../../models/sender-type';

@Component({
  selector: 'app-chat',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink, ClickOutsideDirective, ErrorComponent, PushSubscriptionModalComponent ],
  templateUrl: './chat.component.html',
  styleUrl: './chat.component.scss'
})

export class ChatComponent implements OnInit, OnDestroy {
  @ViewChild('chatContainer') private chatContainer!: ElementRef;
  private organizationId: string = '';
  private conversationId: string = '';
  private joinCode: string = '';
  private accessToken: string = '';
  private messageSub: Subscription | null = null;
  public debugMode: boolean = false;
  public loading: boolean = true;
  public messagesLoading: boolean = false;
  public sendingMessage: boolean = false;
  public conversationReady: boolean = false;
  public conversationHasError: boolean = false;
  public subscribed: boolean = false;
  public showSubscriptionModal: boolean = false;
  public showUnSubscriptionBtn: boolean = false;
  public isIosPWA: boolean = false;
  public isIosNotPWA: boolean = false;
  public isIosSafari: boolean = false;
  public voiceRecordingSupported: boolean = true;
  public messages: chatMessageModel[] = [];
  public newMessage = '';
  public showAttachmentPicker = false;
  public showEmojiPicker = false;
  public eSenderType = senderType
  public advisorProfile: advisorProfileModel = new advisorProfileModel;
  public errorViewModel: ErrorViewModel = new ErrorViewModel;
  public enumMediaType = MediaType;

  //Voice Recording Parameters
  private mediaRecorder!: MediaRecorder;
  private recordChunks: BlobPart[] = [];
  private recordBtnStartX = 0;
  private timerInterval: any;
  private holdingRecordBtn: boolean = false;
  private holdingRecordBtnTimeout: any;
  public recordUploadProgress: number = 0;
  public isRecording = false;
  public recordingTime = 0;

  @ViewChild('recordBtn', { static: false }) recordBtn!: ElementRef<HTMLButtonElement>;

  constructor(
    private _snackBar: MatSnackBar,
    private _dialog: MatDialog,
    private _chatService: ChatService,
    private _Route: ActivatedRoute,
    private _tokenStorage: TokenStorageService,
    private _pushService: PushNotificationService,
    private _mediaService: MediaService,
    private _visibility: AppVisibilityService
  ) {
    if(this.isIosPWA) {
      this._visibility.onResume$.subscribe(() => {
        this.pwaSyncMessages();
      });
    }
  }

  async ngOnInit(): Promise<void> {
    this.isIosPWA = this._pushService.isIosPWA();
    this.isIosNotPWA = this._pushService.isIosNotPWA();
    this.isIosSafari = this._pushService.isIosSafari();

    // Get Parameters
    const matchedPath = this._Route.snapshot.routeConfig?.path;
    if(matchedPath === 'chat'){
      this._Route.queryParams.subscribe(async (params: { [x: string]: string; }) => {
        this.organizationId = params['organizationId'] || '';
        this.conversationId = params['conversationId'] || '';
        this.joinCode = params['joinCode'] || '';
      });

      //If IOS use parameters as route path
      if(this.isIosSafari){
        //window.location.href = `${window.location.origin}/${this.organizationId}/${this.conversationId}/${this.joinCode}/chat`
      }
    }
    else {
      //Get Parameters by route
      this._Route.paramMap.subscribe((params) => {
        this.organizationId = params.get('organizationId') || '';
        this.conversationId = params.get('conversationId') || '';
        this.joinCode = params.get('joinCode') || '';
        this.debugMode = params.get('anything') == 'debug';

        // const matchedPath = this._Route.snapshot.routeConfig?.path;
        // if (matchedPath === ':organizationId/debug') {
        //   this.debugMode = true;
        // }
      });
    }

    // Validate required parameters
    if (!this.conversationId) {
      this.displayErrorMessage('Missing parameter conversationId!');
      throw Error('Missing required parameter! [conversationId]');
    }

    // Check for existing tokens for this conversation
    let storageData = this._tokenStorage.get(this.conversationId);
    let accessToken = storageData?.accessToken;
    let refreshToken = storageData?.refreshToken;

    // Check if browser supports recording
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('This browser does not support audio recording.');
      this.voiceRecordingSupported = false;
    }

    //Check if on IOS and it is not the PWA then stop join conversation
    //Instead display instruction on how to install the PWA
    if(this.isIosNotPWA) {
      this.loading = false;
    }
    else {
      // Fetch conversation history
      try {
        if (!accessToken || !refreshToken) {
          console.log("No access token or refresh token found for conversation, joining conversation");

          if (!this.organizationId || !this.joinCode) {
            this.displayErrorMessage('Missing parameter OrganizationId or joinCode!');
            throw Error('Missing required parameter! [organizationId] [joinCode]');
          }
          
          // Join conversation and get new tokens
          try {
            accessToken = await lastValueFrom(this._chatService.joinConversation(this.organizationId, this.conversationId, this.joinCode));
          }
          catch (error) {
            console.error('Error joining conversation:', error);
            throw error;
          }

          if(!accessToken){
            throw Error('🚫 Failed to get access token!');
          }
        }
        else {
            console.log("Access token and refresh token found for conversation.");
        }

        // Set access token
        this.accessToken = accessToken;

        // Set Access Token in Chat Service
        this._chatService.setAccessToken(this.accessToken);

        // Subscribe to Push Notification
        if(this._pushService.isPushSupported()) {
          console.log("✅ Push Notification supported by Browser!")

          // check Subscription
          this.subscribed = await this._pushService.isSubscribed();
          if (!this.subscribed) {
            console.log("User not subscribed!");
            this.showSubscriptionModal = true;
          }
          else {
            console.log("User already subscribed!");
            this.showUnSubscriptionBtn = true;
          }
        }
        else {
          console.log("🚫 Push Notification not supported by Browser!")
        }

        
        //Get the conversation
        try {
          var history = await lastValueFrom(this._chatService.fetchConversation());
        } catch (error) {
          console.error('Error fetching conversation history:', error);
          throw error;
        }

        if(history.detail){
          // Render Advisor Profile
          this.advisorProfile.name = history.detail.assignedToName;

          //Store Conversation details in storage
          this._tokenStorage.update(this.conversationId, this.advisorProfile.name, '');

          //If already subscribed but conversation subscription date is null or empty,
          //silently send the subscription details to the API
          if(this.subscribed && !history.detail.subscriptionDate){
            console.log("Conversation does not have subscription but user already subscribed with another conversation. Sending the Subscription details to API...")
            await this._pushService.silentSubscribeToPush(this.accessToken)
          }

          //Set style context
          let customizedStyle = "";
          if("[WEBCHAT_PRIMARY_COLOR]" in history.detail.styleContext && history.detail.styleContext["[WEBCHAT_PRIMARY_COLOR]"]) {
            customizedStyle += `--primary-color: ${this.hexToRgb(history.detail.styleContext["[WEBCHAT_PRIMARY_COLOR]"]).join(',')};`;
            if(this.isColorDark(history.detail.styleContext["[WEBCHAT_PRIMARY_COLOR]"])){
              customizedStyle += `--color-over-primary: 255,255,255;`;
            } else {
              customizedStyle += `--color-over-primary: 51,51,51;`;
            }
          }

          if("[WEBCHAT_FONT_FAMILY]" in history.detail.styleContext && history.detail.styleContext["[WEBCHAT_FONT_FAMILY]"]) {
            customizedStyle += `--font-family: ${this.hexToRgb(history.detail.styleContext["[WEBCHAT_FONT_FAMILY]"]).join(',')};`;
          }

          if(customizedStyle){
            let styleNode = document.createElement("style")
            styleNode.innerHTML = ":root{" + customizedStyle + "}";
            document.head.appendChild(styleNode);
          }
        }

        // Render messages in chronological order
        if (history.messages && history.messages.length > 0) {
          this.pushMessages(history.messages);
        }

        //Display Chat content
        this.loading = false;
        this.conversationReady = true;
      } catch (error: any) {
        this.conversationReady = false;
        
        if(error?.error?.errors[0]){
          this.displayErrorMessage(error.error.errors[0].errorDescription);
        }

        throw Error('Failed to fetch conversation history:', error);
      }
    }

    if(this.conversationReady){
      await this._chatService.startConnection();
      this.messageSub = this._chatService.messages$.subscribe(message => {
        if (message) {
          let chatMessage: chatMessageModel = {
            text: message.messageText || '',
            attachments: message.media,
            sendreType: message.senderType,
            time: this.formatAMPM(new Date(message.createdOn))
          }

          this.messages.push(chatMessage);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.messageSub?.unsubscribe();
  }

  ngAfterViewChecked(): void {
    if(this.conversationReady){
      this.scrollToBottom();
    }
  }

  async acceptSubscribe() {
    console.log("Subscription accepted by user.")
    try {
      await Promise.race([
        this._pushService.subscribeToPush(this.accessToken),
        new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Subscribe Timeout!')), 10000))
      ]);
      
      this._snackBar.open('Subscribed to notifications', 'Close', {
        duration: 3000,
        panelClass: ['snackbar-success']
      });

      // Trigger PWA install prompt if available
      // Prompt 5 seconds after subscription
      setTimeout(async () => {
        const installAccepted = await Promise.race([
          this._pushService.promptInstallPWA(),
          new Promise<never>((_, reject) => setTimeout(() => reject(new Error('PWA Install Timeout!')), 10000))
        ]);

        if (installAccepted) {
          this._snackBar.open('App installed successfully!', 'Close', {
            duration: 3000,
            panelClass: ['snackbar-success']
          });
        } else {
          this._snackBar.open('App not installed. You can install the app later from your browser menu.', 'Close', {
            duration: 3000,
            panelClass: ['snackbar-warning']
          });
        }
      }, 5000);
    } catch (err) {
      this._snackBar.open('Failed to subscribe to notifications', 'Close', {
        duration: 3000,
        panelClass: ['snackbar-error']
      });
      console.error(err);
    }
    this.showSubscriptionModal = false;
  }

  skipSubscribe() {
    console.log("Subscription skipped by user.")
    this.showSubscriptionModal = false;
  }

  async unsubscribeFromPush(event: Event) {
    event.preventDefault();

    const dialogRef = this._dialog.open(ConfirmDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirm Unsubscribe',
        message: 'Are you sure you want to unsubscribe from push notifications?',
        cancelBtn: 'Cancel',
        confirmBtn: 'Unsubscribe'
      }
    });

    dialogRef.afterClosed().subscribe(async confirmed => {
      if (!confirmed) return;

      let unsubscribed = await this._pushService.unsubscribeFromPush();
      if(unsubscribed) {
        this._snackBar.open('Unsubscribed from notifications.', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success']
        });

        this.showUnSubscriptionBtn = false;
      }
      else {
        this._snackBar.open('Something went wrong!', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error']
        });
      }
    });
  }
  
  async sendMessage(isRecording: boolean): Promise<void> {
    if(this.sendingMessage) return;
    if(isRecording && this.isRecording){
      this.stopRecording();
    }
    else if (!isRecording && this.newMessage.trim()) {
      let message = this.newMessage.trim();
      this.newMessage = '';
      this.sendingMessage = true;
      try {
        await lastValueFrom(await this._chatService.sendMessage(message));
      }
      catch (err) {
        this._snackBar.open('Failed to send new message', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error']
        });
        console.error("Failed to send new message", err);
      }
      this.newMessage = '';
      this.sendingMessage = false;
    }
  }

  async pwaSyncMessages() {
    this.messagesLoading = true;
    let messages = await lastValueFrom(this._chatService.fetchConversationHistory());
    if(messages && messages.length > 0) {
      this.pushMessages(messages)
    }
    this.messagesLoading = false;
  }

  pushMessages(messages: ConversationMessage[]) {
    this.messages = [];
    messages.sort((a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime())
      .forEach(message => {
        this.messages.push({
          text: message.messageText,
          attachments: message.media,
          sendreType: message.senderType,
          time: this.formatAMPM(new Date(message.createdOn)) });
      });
  }

  toggleAttachmentPicker() {
    this.showAttachmentPicker = !this.showAttachmentPicker;
  }

  toggleEmojiPicker() {
    this.showEmojiPicker = !this.showEmojiPicker;
  }

  addEmoji(emoji: string) {
    this.newMessage += emoji;
    this.showEmojiPicker = false;
  }

  openAttachmentDialog(type: MediaType) {
    this.showAttachmentPicker = false;
    const dialogRef = this._dialog.open(AttachmentDialogComponent, {
      width: '500px',
      data: { type: type, accessToken : this.accessToken }
    });

    dialogRef.afterClosed().subscribe(async result => {
      if (result) {
        try {
          await lastValueFrom(await this._chatService.sendMessage(result.newMessage.trim(), result.uploadedFiles));
        }
        catch (err) {
          this._snackBar.open('Failed to send new message', 'Close', {
            duration: 3000,
            panelClass: ['snackbar-error']
          });
          console.error("Failed to send new message", err);
        }
      }
    });
  }

  getAttachmentGridClass(attachments:MediaModel[]): string {
    if(attachments && attachments.length > 0) {
      if(attachments.length == 1 || attachments[0]?.mediaType == MediaType.document) {
        return 'one-per-row';
      }
      return attachments.length % 3 === 0 ? 'three-per-row' : 'two-per-row';
    }
    return "";
  }

  openImage(images: MediaModel[], index: number): void {
    this._dialog.open(ImageViewerDialogComponent, {
      data: { images, startIndex: index },
      panelClass: 'full-screen-dialog'
    });
  }




  /* Voice Message */
  public recordingMouseDown = (event: MouseEvent) => {
    //Accept only left click
    if (event.button !== 0) {
      event.preventDefault();
      return;
    }

    this.holdingRecordBtn = false;
    this.recordBtnStartX = event.clientX;

    // set a timeout to detect hold
    this.holdingRecordBtnTimeout = setTimeout(() => {
      this.holdingRecordBtn = true;
      document.addEventListener("mousemove", this.recordingMouseMove);
      document.addEventListener("mouseup", this.recordingMouseUpTouchEnd);
      this.startRecording();
    }, 300);
  };

  public recordingTouchStart = (event: TouchEvent) => {
    this.holdingRecordBtn = false;
    this.recordBtnStartX = event.touches[0].clientX;

    setTimeout(() => {
      this.holdingRecordBtn = true;
      document.addEventListener("touchmove", this.recordingTouchMove);
      document.addEventListener("touchend", this.recordingMouseUpTouchEnd);
      this.startRecording();
    }, 300);
  };

  private recordingMouseMove = (event: MouseEvent) => {
    if (!this.isRecording || !this.holdingRecordBtn) return;

    if (this.recordBtnStartX - event.clientX > 100) {
      this.cancelRecording();
    }
  };

  private recordingTouchMove = (event: TouchEvent) => {
    if (!this.isRecording || !this.holdingRecordBtn) return;

    if (this.recordBtnStartX - event.touches[0].clientX > 100) {
      this.cancelRecording();
    }
  };

  public recordingMouseUpTouchEnd = () => {
    clearTimeout(this.holdingRecordBtnTimeout);

    if(this.holdingRecordBtn){
      this.stopRecording();
    }
    else if(!this.isRecording){
      this.startRecording();
    }
  };

  async startRecording() {
    navigator.mediaDevices.getUserMedia({ audio: true }).then(stream => {
      this.isRecording = true;
      this.recordingTime = 0;
      this.timerInterval = setInterval(() => this.recordingTime++, 1000);

      this.mediaRecorder = new MediaRecorder(stream);
      this.recordChunks = [];

      this.mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this.recordChunks.push(event.data);
        }
      };

      this.mediaRecorder.start();
      console.log('Recording started');
    })
    .catch(err => {
      console.error('Microphone access denied:', err);

      this._dialog.open(ConfirmDialogComponent, {
        width: '400px',
        data: {
          title: 'Microphone permission',
          message: 'To record voice messages, need access to microphone. Please allow access to microphone by clicking on URL bar.',
          cancelBtn: '',
          confirmBtn: 'OK. got it'
        }
      });
    });
  }

  stopRecording() {
    if (!this.mediaRecorder || !this.isRecording) return;

    this.holdingRecordBtn = false;
    this.mediaRecorder.stop();

    this.mediaRecorder.onstop = () => {
      const blob = new Blob(this.recordChunks, { type: 'audio/webm' });
      this.uploadVoiceMessage(blob);
    };

    this.isRecording = false;
    clearInterval(this.timerInterval);

    // remove global listeners
    document.removeEventListener("mousemove", this.recordingMouseMove);
    document.removeEventListener("mouseup", this.recordingMouseUpTouchEnd);
    document.removeEventListener("touchmove", this.recordingTouchMove);
    document.removeEventListener("touchend", this.recordingMouseUpTouchEnd);
  }

  cancelRecording() {
    if (!this.isRecording) return;

    this.isRecording = false;
    this.mediaRecorder.stop();
    this.recordChunks = [];
    clearInterval(this.timerInterval);

    // remove global listeners
    document.removeEventListener("mousemove", this.recordingMouseMove);
    document.removeEventListener("mouseup", this.recordingMouseUpTouchEnd);
    document.removeEventListener("touchmove", this.recordingTouchMove);
    document.removeEventListener("touchend", this.recordingMouseUpTouchEnd);

    console.log("Recording cancelled ❌");
  }

  private uploadVoiceMessage(blob: Blob) {
    const audioFile = new File([blob], 'voice-message.webm', { type: 'audio/webm' });

    this._mediaService.uploadFile(audioFile, this.accessToken, MediaType.audio)
      .subscribe({
        next: async ({ progress, result }) => {
          this.recordUploadProgress = progress;

          if (result) {
            try {
              await lastValueFrom(await this._chatService.sendMessage('', [{ mediaType: MediaType.audio, path: result.path, fileName: result.fileName }]));
            }
            catch (err) {
              this._snackBar.open('Failed to send new message', 'Close', {
                duration: 3000,
                panelClass: ['snackbar-error']
              });
              console.error("Failed to send new message", err);
            }

            this.recordUploadProgress = 0;
          }
        },
        error: (err) => {
          console.error('Audio upload failed', err);
          this.recordUploadProgress = 0;
        }
      });
  }

  private displayErrorMessage(message: string): void{
    this.errorViewModel.message = message;
    this.errorViewModel.errorTime = new Date().toDateString();
    this.conversationHasError = true;
    this.loading = false;
  }

  private scrollToBottom(): void {
    try {
      this.chatContainer.nativeElement.scrollTop = this.chatContainer.nativeElement.scrollHeight;
    } catch (err) {
      console.error('Failed to scroll:', err);
    }
  }

  private formatAMPM(date: Date): string {
    let hours = date.getHours();
    let minutes: any = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    minutes = minutes < 10 ? '0' + minutes : minutes;
    return hours + ':' + minutes + ' ' + ampm;
  }

  private hexToRgb(hex:string){
    var c = [];
    if(/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)){
        c= hex.substring(1).split('');
        if(c.length == 3){
            c= [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        let x:number= +('0x'+c.join(''));
        return [(x>>16)&255, (x>>8)&255, x&255];
    }
    return [];
  }

  private isColorDark(color:string):boolean {
      var r, g, b, hsp, c;
      if (color.match(/^rgb/)) {
          c = color.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*(\d+(?:\.\d+)?))?\)$/)?.slice(1) || [];
      }
      else {
          c = this.hexToRgb(color);
      }
      r = +c[0];
      g = +c[1];
      b = +c[2];

      hsp = Math.sqrt(
      0.299 * (r * r) +
      0.587 * (g * g) +
      0.114 * (b * b)
      );
  
      return hsp <= 127.5;
  }
}